import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../common/Card';
import Badge from '../common/Badge';
import SafeIcon from '../../common/SafeIcon';
import Logo from '../common/Logo';

const workflowSteps = [
  { id: 1, title: 'Node Integration', desc: 'Secure handshake with Shopify, Amazon, and ERP endpoints.', icon: 'Cpu', detail: 'StockPilot establishes encrypted tunnels to ingest raw transaction logs and inventory snapshots every 60 seconds.' },
  { id: 2, title: 'Signal Cleaning', desc: 'Removing noise from seasonal spikes and returns.', icon: 'Activity', detail: 'Our algorithms filter out non-representative demand signals to ensure the forecast isn\'t skewed by one-off viral events.' },
  { id: 3, title: 'Predictive Modeling', desc: 'Calculating the "Burn Rate" per SKU across all channels.', icon: 'TrendingUp', detail: 'We project exactly when you will hit zero stock based on current velocity, lead times, and supplier reliability.' },
  { id: 4, title: 'Risk Assessment', desc: 'Identifying critical reorder windows and deficits.', icon: 'AlertTriangle', detail: 'Prioritizes capital allocation to high-margin SKUs that are at risk of stockout within the next 72 hours.' },
  { id: 5, title: 'Zone Optimization', desc: 'Dynamic slotting for maximum pick-efficiency.', icon: 'Map', detail: 'Recommends moving high-velocity items to "Hot Zones" near the packing floor to reduce labor costs by up to 15%.' },
  { id: 6, title: 'Vendor Batching', desc: 'Automated PO generation with cost-basis logic.', icon: 'ShoppingCart', detail: 'Batches items from the same supplier to meet Free Freight Thresholds (FFT) and volume discount tiers.' },
  { id: 7, title: 'Ledger Audit', desc: 'Immutable record of every inventory adjustment.', icon: 'List', detail: 'Every automated move and manual adjustment is logged to a secure ledger for quarterly compliance and financial auditing.' },
];

const faqs = [
  { q: "How does StockPilot connect to my existing tech stack?", a: "We use secure API handshakes with major platforms like Shopify, NetSuite, and Amazon. Integration usually takes less than 15 minutes with no custom coding required." },
  { q: "Is the demo data representative of real-world scenarios?", a: "Yes. The sample data reflects realistic volatility, lead times (3-21 days), and multi-zone warehouse layouts used by mid-market ecommerce brands." },
  { q: "How is my data privacy handled?", a: "StockPilot uses enterprise-grade encryption. In this demo, all data is localized to your browser session. No private data is ever transmitted to external servers." },
  { q: "Can I customize the reorder logic?", a: "Absolutely. You can set custom safety stock buffers, lead-time offsets, and priority rankings for every SKU in your catalog." },
  { q: "What happens if a supplier is late?", a: "The system automatically adjusts the 'Reliability Score' for that vendor and pulls forward the next reorder alert to compensate for the drift." }
];

const DemoView = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [simState, setSimState] = useState('success');
  const [filterType, setFilterType] = useState('all');
  const [isRunning, setIsRunning] = useState(false);

  const handleRunStep = () => {
    setIsRunning(true);
    setTimeout(() => setIsRunning(false), 1200);
  };

  const sampleData = [
    { sku: 'SP-1092', name: 'Titan Mech Keyboard', type: 'HV', status: 'Optimal', stock: 412, zone: 'A1', lead: '4d' },
    { sku: 'SP-2281', name: 'Ergo-Lift Desk', type: 'Bulk', status: 'Low', stock: 18, zone: 'C4', lead: '14d' },
    { sku: 'SP-0092', name: 'Braided 4K Cable', type: 'HV', status: 'Critical', stock: 2, zone: 'B2', lead: '2d' },
    { sku: 'SP-7731', name: 'Acoustic Barrier', type: 'Bulk', status: 'Optimal', stock: 150, zone: 'C1', lead: '21d' },
    { sku: 'SP-4410', name: 'Pro-Stream Cam', type: 'HV', status: 'Optimal', stock: 89, zone: 'A1', lead: '5d' },
  ];

  const filteredData = filterType === 'all' ? sampleData : sampleData.filter(d => d.type === filterType);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-12 md:space-y-20 pb-24 max-w-full overflow-hidden">
      
      {/* 1. Overview / What it does */}
      <section className="space-y-6">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-10 border-b border-sp-gray-800 pb-12">
          <div className="max-w-3xl">
            <Badge variant="lime" className="mb-4">SYSTEM OVERVIEW</Badge>
            <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.1]">
              The Intelligent Command Center for <span className="text-sp-lime shadow-neon">Stock-to-Cash</span> Optimization.
            </h1>
            <p className="text-gray-400 mt-6 text-base md:text-lg leading-relaxed">
              StockPilot is a B2B SaaS ecosystem designed for ecommerce and wholesale operators. 
              We solve the "Capital Trap"—the loss of revenue caused by stockouts and the loss of cash flow caused by overstocking. 
              By centralizing demand signals, we provide a unified warehouse control panel that automates reordering and optimizes zone placement.
            </p>
          </div>
          <div className="w-full lg:w-96 shrink-0 space-y-4">
            <div className="aspect-video bg-sp-gray-900 border border-sp-gray-700 rounded-xl overflow-hidden relative group">
              <img 
                src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=600&q=80" 
                alt="Automated Warehouse" 
                className="w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                 <Logo className="w-16 h-16 opacity-80" />
              </div>
              <div className="absolute bottom-3 left-3 flex items-center gap-2">
                 <div className="w-2 h-2 rounded-full bg-sp-lime animate-pulse"></div>
                 <span className="text-[10px] text-sp-lime font-mono uppercase tracking-widest">Live Facility Feed</span>
              </div>
            </div>
            <div className="bg-sp-gray-900 border border-sp-gray-800 p-4 rounded-lg flex justify-between items-center">
               <div className="text-[10px] text-gray-500 font-mono uppercase">Version Instance</div>
               <div className="text-white font-mono text-xs">v2.4.9 // STABLE_READY</div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Demo / How It Works */}
      <section className="space-y-8">
        <div className="flex items-center gap-3">
          <div className="h-4 w-1 bg-sp-lime shadow-neon"></div>
          <h3 className="text-xs font-mono text-gray-400 uppercase tracking-[0.3em]">Demo / How It Works</h3>
        </div>

        <div className="bg-sp-gray-900 border border-sp-gray-700 rounded-2xl overflow-hidden shadow-2xl">
          <div className="p-6 md:p-10 border-b border-sp-gray-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
             <div>
               <h4 className="text-xl font-bold text-white mb-2">Try this flow</h4>
               <p className="text-sm text-gray-500">Select an operational stage to view the underlying logic and system response.</p>
             </div>
             <Badge variant="default" className="w-fit">SIMULATION_MODE</Badge>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-4 border-r border-sp-gray-800 p-2 space-y-1 bg-sp-black/20">
              {workflowSteps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`w-full text-left p-4 rounded-lg transition-all flex items-center gap-4 group
                    ${activeStep === step.id ? 'bg-sp-gray-800 text-sp-lime shadow-inner border border-sp-gray-700' : 'text-gray-500 hover:text-gray-300 hover:bg-sp-gray-800/30'}`}
                >
                  <div className={`w-10 h-10 rounded flex items-center justify-center border ${activeStep === step.id ? 'border-sp-lime bg-sp-lime/10 shadow-neon' : 'border-sp-gray-700 bg-sp-black'}`}>
                    <SafeIcon name={step.icon} className="text-lg" />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <div className="text-xs font-bold uppercase tracking-tight truncate">{step.title}</div>
                    <div className="text-[10px] opacity-60 font-mono">MODULE_0{step.id}</div>
                  </div>
                  {activeStep === step.id && <SafeIcon name="ChevronRight" className="text-sp-lime animate-pulse" />}
                </button>
              ))}
            </div>

            <div className="lg:col-span-8 p-6 md:p-16 bg-sp-black/40 flex flex-col justify-between min-h-[500px] relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-5">
                 <Logo className="w-48 h-48" glow={false} />
              </div>
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-8 relative z-10"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-1 bg-sp-lime"></div>
                    <span className="text-sp-lime font-mono text-[10px] uppercase tracking-widest font-bold">Process Node {activeStep}</span>
                  </div>
                  
                  <h5 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                    {workflowSteps[activeStep-1].desc}
                  </h5>
                  
                  <p className="text-gray-400 leading-relaxed text-base md:text-lg max-w-2xl">
                    {workflowSteps[activeStep-1].detail}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6">
                    <div className="p-5 bg-sp-gray-900/80 rounded-xl border border-sp-gray-800 backdrop-blur-sm">
                       <div className="text-[10px] text-gray-500 font-mono uppercase mb-2">Network Latency</div>
                       <div className="text-2xl font-mono text-white">0.024<span className="text-sp-lime text-xs ml-1">ms</span></div>
                    </div>
                    <div className="p-5 bg-sp-gray-900/80 rounded-xl border border-sp-gray-800 backdrop-blur-sm">
                       <div className="text-[10px] text-gray-500 font-mono uppercase mb-2">Node Encryption</div>
                       <div className="text-xs font-mono text-sp-cyan flex items-center gap-2">
                         <SafeIcon name="Shield" /> AES_256_GCM
                       </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-12 pt-8 border-t border-sp-gray-800 flex flex-col sm:flex-row items-center gap-6 relative z-10">
                <button 
                  onClick={handleRunStep}
                  disabled={isRunning}
                  className="w-full sm:w-fit px-10 py-4 bg-sp-lime text-sp-black font-bold uppercase tracking-widest rounded transition-all flex items-center justify-center gap-3 hover:shadow-neon-strong group overflow-hidden relative"
                >
                  <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500"></div>
                  {isRunning ? <SafeIcon name="Loader" className="animate-spin" /> : <SafeIcon name="Play" />}
                  {isRunning ? 'Processing...' : 'Initialize Sequence'}
                </button>
                <div className="text-[10px] font-mono text-gray-600 uppercase tracking-widest text-center sm:text-left">
                  Ready for deployment in production environment.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Sample Data & 4. Simulated States */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-12 md:gap-16">
        
        {/* Sample Data Section */}
        <section className="space-y-8">
          <div className="flex items-center justify-between border-b border-sp-gray-800 pb-4">
            <div className="flex items-center gap-3">
              <div className="h-4 w-1 bg-sp-cyan shadow-[0_0_10px_rgba(0,240,255,0.3)]"></div>
              <h3 className="text-xs font-mono text-gray-400 uppercase tracking-[0.3em]">Sample Data</h3>
            </div>
            <div className="flex gap-1 bg-sp-gray-900 p-1 rounded border border-sp-gray-800">
               {['all', 'HV', 'Bulk'].map(t => (
                 <button 
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={`px-3 py-1 text-[10px] uppercase font-mono rounded transition-all ${filterType === t ? 'bg-sp-gray-800 text-sp-lime border border-sp-gray-700' : 'text-gray-500 hover:text-gray-300'}`}
                 >
                   {t}
                 </button>
               ))}
            </div>
          </div>

          <Card className="overflow-hidden border-sp-gray-800 bg-sp-black/20">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-sp-gray-800 bg-sp-gray-800/30 text-[10px] uppercase tracking-wider text-gray-500 font-mono">
                    <th className="p-4 font-medium">SKU Node</th>
                    <th className="p-4 font-medium">Zone</th>
                    <th className="p-4 font-medium">Stock</th>
                    <th className="p-4 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="text-xs font-mono">
                  {filteredData.map((item, idx) => (
                    <tr key={item.sku} className="border-b border-sp-gray-800 hover:bg-sp-gray-800/50 transition-colors group">
                      <td className="p-4">
                        <div className="text-white font-bold group-hover:text-sp-lime transition-colors">{item.sku}</div>
                        <div className="text-[10px] text-gray-600 truncate w-32">{item.name}</div>
                      </td>
                      <td className="p-4">
                         <span className="px-2 py-0.5 bg-sp-gray-900 border border-sp-gray-800 rounded text-gray-400">{item.zone}</span>
                      </td>
                      <td className="p-4 text-gray-300">{item.stock} <span className="text-[9px] text-gray-600">UNITS</span></td>
                      <td className="p-4 text-right">
                        <Badge variant={item.status === 'Critical' ? 'alert' : item.status === 'Low' ? 'default' : 'lime'}>
                          {item.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>

        {/* Simulated States Section */}
        <section className="space-y-8">
          <div className="flex items-center gap-3 border-b border-sp-gray-800 pb-4">
            <div className="h-4 w-1 bg-sp-alert shadow-neon-alert"></div>
            <h3 className="text-xs font-mono text-gray-400 uppercase tracking-[0.3em]">Simulated States</h3>
          </div>

          <div className="bg-sp-gray-900 border border-sp-gray-800 rounded-xl p-6 space-y-6">
            <div className="flex flex-wrap gap-2">
              {['success', 'warning', 'empty', 'loading', 'error'].map(state => (
                <button
                  key={state}
                  onClick={() => setSimState(state)}
                  className={`px-4 py-2 text-[10px] font-mono uppercase rounded border transition-all
                    ${simState === state ? 'bg-sp-gray-700 border-white text-white shadow-lg' : 'bg-sp-black border-sp-gray-800 text-gray-500 hover:border-sp-gray-600'}`}
                >
                  {state}
                </button>
              ))}
            </div>

            <div className="h-64 bg-sp-black rounded-lg border border-sp-gray-800 flex items-center justify-center p-6 text-center relative overflow-hidden group">
               <div className="absolute inset-0 opacity-5 bg-grid-pattern"></div>
               <AnimatePresence mode="wait">
                 <motion.div
                    key={simState}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    className="relative z-10 w-full"
                 >
                    {simState === 'success' && (
                      <div className="space-y-4">
                        <div className="w-16 h-16 bg-sp-lime/10 border border-sp-lime/30 rounded-full flex items-center justify-center mx-auto text-sp-lime shadow-neon">
                          <SafeIcon name="CheckCircle" className="text-3xl" />
                        </div>
                        <div className="text-white font-bold text-lg">Sequence Approved</div>
                        <p className="text-xs text-gray-500 font-mono">PO-2024-X-001 has been transmitted to vendor.</p>
                      </div>
                    )}
                    {simState === 'warning' && (
                      <div className="space-y-4">
                        <div className="w-16 h-16 bg-orange-500/10 border border-orange-500/30 rounded-full flex items-center justify-center mx-auto text-orange-400">
                          <SafeIcon name="AlertTriangle" className="text-3xl" />
                        </div>
                        <div className="text-white font-bold text-lg">Drift Alert</div>
                        <p className="text-xs text-gray-500 font-mono">Inventory mismatch detected in Zone A2.</p>
                      </div>
                    )}
                    {simState === 'empty' && (
                      <div className="space-y-4 opacity-50">
                        <SafeIcon name="Inbox" className="text-5xl text-sp-gray-800 mx-auto" />
                        <div className="text-gray-500 font-bold text-lg">Buffer Cleared</div>
                        <p className="text-xs text-gray-600 font-mono">No pending signals in the current queue.</p>
                      </div>
                    )}
                    {simState === 'loading' && (
                      <div className="space-y-6 w-48 mx-auto">
                        <SafeIcon name="Loader" className="text-4xl text-sp-lime animate-spin mx-auto" />
                        <div className="h-1.5 w-full bg-sp-gray-900 rounded-full overflow-hidden border border-sp-gray-800">
                          <motion.div 
                            className="h-full bg-sp-lime shadow-neon" 
                            animate={{ x: ['-100%', '100%'] }} 
                            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }} 
                            style={{ width: '40%' }} 
                          />
                        </div>
                        <div className="text-[10px] text-sp-lime font-mono animate-pulse tracking-widest uppercase">Recalibrating Node</div>
                      </div>
                    )}
                    {simState === 'error' && (
                      <div className="space-y-4">
                        <div className="w-16 h-16 bg-sp-alert/10 border border-sp-alert/30 rounded-full flex items-center justify-center mx-auto text-sp-alert shadow-neon-alert">
                          <SafeIcon name="XOctagon" className="text-3xl" />
                        </div>
                        <div className="text-white font-bold text-lg">Critical Timeout</div>
                        <p className="text-xs text-gray-500 font-mono">Amazon SP-API gateway is unresponsive.</p>
                      </div>
                    )}
                 </motion.div>
               </AnimatePresence>
            </div>
          </div>
        </section>
      </div>

      {/* 5. FAQ Section */}
      <section className="space-y-8">
        <div className="flex items-center gap-3">
          <div className="h-4 w-1 bg-white shadow-[0_0_10px_rgba(255,255,255,0.3)]"></div>
          <h3 className="text-xs font-mono text-gray-400 uppercase tracking-[0.3em]">FAQ / Knowledge Base</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {faqs.map((faq, idx) => (
            <div key={idx} className="p-8 bg-sp-gray-900/50 border border-sp-gray-800 rounded-2xl hover:border-sp-gray-600 transition-all group hover:bg-sp-gray-900">
              <h5 className="text-white font-bold mb-4 flex items-start gap-3 group-hover:text-sp-lime transition-colors">
                <span className="text-sp-lime font-mono opacity-50">Q{idx+1}.</span> {faq.q}
              </h5>
              <p className="text-sm text-gray-400 leading-relaxed pl-8">
                {faq.a}
              </p>
            </div>
          ))}
          <div className="p-8 border-2 border-dashed border-sp-gray-800 rounded-2xl flex flex-col items-center justify-center text-center space-y-4">
             <SafeIcon name="MessageSquare" className="text-3xl text-gray-700" />
             <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">Need Support?</div>
             <p className="text-xs text-gray-600 font-mono">24/7 Priority Assistance for Enterprise Nodes.</p>
          </div>
        </div>
      </section>

      {/* Final Credit Footer */}
      <footer className="pt-24 pb-12 border-t border-sp-gray-800 text-center space-y-8 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-sp-black px-6">
           <Logo className="w-12 h-12" glow={true} />
        </div>
        
        <div className="flex justify-center items-center gap-8 opacity-40 grayscale group hover:grayscale-0 transition-all">
           <div className="text-[10px] font-mono text-white flex items-center gap-2 uppercase tracking-widest">
              <SafeIcon name="Shield" /> Certified Partner
           </div>
           <div className="text-[10px] font-mono text-white flex items-center gap-2 uppercase tracking-widest">
              <SafeIcon name="Globe" /> Global Logistics
           </div>
        </div>

        <div className="space-y-4">
          <div className="text-[10px] text-gray-600 font-mono uppercase tracking-[0.5em]">StockPilot Deployment: Demo_Build_v2.4.9</div>
          <div className="text-xs text-gray-500 font-medium">Made by <span className="text-gray-300">LingLink AI</span> and <span className="text-gray-300">DigLab AI</span></div>
        </div>
      </footer>
    </motion.div>
  );
};

export default DemoView;